import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Loader2, Send } from "lucide-react";
import { Switch } from "./ui/switch";
import { pushSupported, getExistingSubscription, subscribeToPush, unsubscribeFromPush, sendTestPush } from "../lib/push";

export const PushSettings = ({ compact = false }) => {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const supported = pushSupported();

  useEffect(() => {
    if (supported) getExistingSubscription().then((s) => setEnabled(Boolean(s))).catch(() => {});
  }, [supported]);

  const toggle = async (v) => {
    setBusy(true);
    try {
      if (v) {
        await subscribeToPush();
        setEnabled(true);
        toast.success("Push notifications enabled on this device");
      } else {
        await unsubscribeFromPush();
        setEnabled(false);
        toast.success("Push notifications disabled on this device");
      }
    } catch (err) {
      toast.error(err.message || "Could not update push settings");
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const res = await sendTestPush();
      if (res.success) toast.success("Test notification sent — check your device");
      else toast.error(res.error || "Test failed");
    } catch {
      toast.error("Test failed");
    } finally {
      setTesting(false);
    }
  };

  if (!supported) {
    return (
      <div data-testid="push-settings" className={compact ? "" : "bg-[#121212] border border-zinc-800 rounded-xl p-6"}>
        <p className="text-zinc-500 text-sm">Push notifications aren't supported in this browser. On iPhone, add this site to your Home Screen first (Share → Add to Home Screen).</p>
      </div>
    );
  }

  return (
    <div data-testid="push-settings" className={compact ? "" : "bg-[#121212] border border-zinc-800 rounded-xl p-6"}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <BellRing className="h-5 w-5 text-blue-400" />
          <div>
            <p className="text-white text-sm font-semibold">Push notifications on this device</p>
            <p className="text-zinc-500 text-xs">Get alerts even when this site is closed</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {busy && <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />}
          <Switch data-testid="push-toggle" checked={enabled} onCheckedChange={toggle} disabled={busy} />
        </div>
      </div>
      {enabled && (
        <button data-testid="push-test-btn" onClick={test} disabled={testing} className="mt-4 inline-flex items-center gap-1.5 border border-zinc-700 text-zinc-300 hover:text-white rounded-sm px-3 py-1.5 text-xs font-semibold disabled:opacity-50">
          {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send test notification
        </button>
      )}
      <p className="text-zinc-600 text-[11px] mt-3">iPhone: add this site to your Home Screen (Share → Add to Home Screen), then enable here. Android &amp; desktop work directly.</p>
    </div>
  );
};
